import { useState } from 'react';
import { usePFRatioHistory, PFHistoryPoint } from '@/hooks/usePFRatioHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import { formatPrice } from '@/lib/format';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';

interface PFRatioHistoryChartProps {
  slug: string;
  geckoId: string | null;
  protocolName: string;
}

const PERIOD_OPTIONS = [
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: '180d', value: 180 },
  { label: '1y', value: 365 },
];

export function PFRatioHistoryChart({ slug, geckoId, protocolName }: PFRatioHistoryChartProps) {
  const [days, setDays] = useState(90);
  const { data, isLoading, error } = usePFRatioHistory(slug, geckoId, days);

  // Calculate period change
  const periodChange = (() => {
    if (!data?.data || data.data.length < 2) return null;
    const first = data.data[0].pf_ratio;
    const last = data.data[data.data.length - 1].pf_ratio;
    if (!first || !last) return null;
    return ((last - first) / first) * 100;
  })();

  const getTrendIcon = () => {
    if (periodChange === null) return <Minus className="h-3 w-3" />;
    if (periodChange > 5) return <TrendingUp className="h-3 w-3 text-destructive" />; // Higher P/F = more expensive
    if (periodChange < -5) return <TrendingDown className="h-3 w-3 text-success" />; // Lower P/F = cheaper
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = () => {
    if (periodChange === null) return 'text-muted-foreground';
    if (periodChange > 5) return 'text-destructive';
    if (periodChange < -5) return 'text-success';
    return 'text-muted-foreground';
  };

  if (!geckoId) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>Gráfico P/F indisponível: protocolo sem mapeamento CoinGecko.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.data?.length) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>Dados históricos de P/F não disponíveis para {protocolName}.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Format data for chart
  const chartData = data.data.map((point) => ({
    ...point,
    dateLabel: new Date(point.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
  }));

  // Calculate average P/F for reference line
  const avgPF = chartData.reduce((sum, p) => sum + p.pf_ratio, 0) / chartData.length;

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">P/F Ratio Histórico</CardTitle>
            {periodChange !== null && (
              <Badge variant="outline" className={getTrendColor()}>
                {getTrendIcon()}
                <span className="ml-1">
                  {periodChange > 0 ? '+' : ''}{periodChange.toFixed(1)}%
                </span>
              </Badge>
            )}
          </div>
          
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={days === option.value ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setDays(option.value)}
                className="text-xs px-2"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pfGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
              
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}x`}
                domain={['auto', 'auto']}
              />
              
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatPrice(v)}
                domain={['auto', 'auto']}
              />
              
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                formatter={(value: any, name: string) => {
                  if (name === 'pf_ratio') return [`${value}x`, 'P/F Ratio'];
                  if (name === 'price') return [formatPrice(value), 'Preço'];
                  return [value, name];
                }}
                labelFormatter={(label) => label}
              />
              
              <ReferenceLine
                yAxisId="left"
                y={avgPF}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="5 5"
                strokeOpacity={0.5}
              />
              
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="price"
                stroke="hsl(var(--muted-foreground))"
                strokeOpacity={0.55}
                strokeWidth={2}
                dot={false}
                activeDot={false}
              />
              
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="pf_ratio"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        
        <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
          <span>
            P/F médio: <span className="font-mono text-foreground">{avgPF.toFixed(1)}x</span>
          </span>
          <span>
            {data.coverage.result_points} pontos de dados
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
